/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';

import { DimensionScaler } from '../../../../core/device/DimensionScaler';
import { Channel } from '../../../../core/entities';
import { InteractiveComponents } from '../../../../core/enums';
import { CursorManager } from '../../../../core/navigation';
import {
    RootState,
    useTivoDispatch,
    useTivoSelector,
} from '../../../../core/tivo/tivo-redux-wrapper';
import { TChannelV2 } from '../../../../core/types';
import { updateOneLineGuideInactivityTimeout } from '../../../../store/videoplayerui/onelineguide/onelineguide.reducers';
import { Caret, Mousetrap } from '../../../common';

import './OneLineGuideInteractiveLayer.scss';

const FAVORITES_MIN_CHANNELS_IN_LIST = 3;
const FAVORITES_MAX_CHANNELS_IN_LIST = 8;
const DEFAULT_OLG_SELECTED_CHANNEL_TOP_OFFSET = 184;
const DEFAULT_MAX_CHANNELS_IN_LIST = 8;
const CHANNEL_HEIGHT = 54;
const CHANNEL_GAP = 12;
const CHANNEL_CONTAINER_PADDING = 4;
interface OneLineGuideInteractiveLayerProps {
    activeComponent: string;
    pastMode: boolean;
    isMovie: boolean;
    visibleLeftCaret?: boolean;
    visibleRightCaret?: boolean;
}

const OneLineGuideInteractiveLayer: FunctionComponent<OneLineGuideInteractiveLayerProps> = ({
    activeComponent,
    pastMode,
    isMovie,
    visibleLeftCaret,
    visibleRightCaret,
}): JSX.Element => {
    const dispatch = useTivoDispatch();
    const isActive = useCallback(
        (component: string) => activeComponent === component,
        [activeComponent],
    );
    const {
        oneLineGuideTurboScroll,
        channels,
        selectedChannel,
        oneLineGuideTurboScrollDirection,
        areChannelsSelected,
    } = useTivoSelector((state: RootState) => state.oneLineGuide);
    const isCursorOn = CursorManager.isCursorAppeared();
    const selectedChannelClassName = 'olg-selected-channel';
    const selectedCellClassName = 'olg-selected-cell';

    const [selectedChannelIndex, setSelectedChannelIndex] = useState<number | null>(null);

    const olgSelectedChannelTopOffset = useMemo(() => {
        /*
         * In cursor mode, when the favorite filter is active and there are fewer than 8 channels,
         * the selected channel cannot be centered in the middle of the channel list.
         * Therefore, instead of setting a fixed top offset to Mousetrap, we need to calculate
         * the offset dynamically based on the selected channel's index.
         */
        if (
            !isCursorOn ||
            !areChannelsSelected ||
            channels.length >= DEFAULT_MAX_CHANNELS_IN_LIST
        ) {
            return DimensionScaler.getInstance().getScaledHeight(
                DEFAULT_OLG_SELECTED_CHANNEL_TOP_OFFSET,
            );
        }
        const index = channels.findIndex(
            (channel: TChannelV2 | Channel) => channel.channelId === selectedChannel?.channelId,
        );
        if (index === -1) {
            return DimensionScaler.getInstance().getScaledHeight(
                DEFAULT_OLG_SELECTED_CHANNEL_TOP_OFFSET,
            );
        }

        return DimensionScaler.getInstance().getScaledHeight(
            index * CHANNEL_HEIGHT + index * CHANNEL_GAP + CHANNEL_CONTAINER_PADDING,
        );
    }, [areChannelsSelected, channels, selectedChannel, isCursorOn]);
    useEffect(() => {
        const index = channels.findIndex(
            (channel: TChannelV2 | Channel) => channel.channelId === selectedChannel?.channelId,
        );
        setSelectedChannelIndex(index);
    }, [channels, selectedChannel]);

    useEffect(() => {
        const possibleActiveComponentsInOlg = [
            ...Object.values(InteractiveComponents),
            selectedChannelClassName,
            selectedCellClassName,
        ];

        if (possibleActiveComponentsInOlg.includes(activeComponent)) {
            dispatch(updateOneLineGuideInactivityTimeout());
        }
    }, [activeComponent, dispatch]);

    const getClassNames = useCallback(
        (direction: 'left' | 'right') => {
            const classNames: string[] = [];

            // Determine base class name based on direction
            const baseClass = direction === 'right' ? 'olg-east' : 'olg-west';
            classNames.push(baseClass);

            // Determine active class logic
            const isActiveClass =
                direction === 'right'
                    ? isActive(InteractiveComponents.EAST)
                    : isActive(InteractiveComponents.WEST);

            if (isActiveClass && !oneLineGuideTurboScroll) {
                classNames.push('active');
            }

            switch (direction) {
                case 'right': // EAST logic
                    if (!pastMode) {
                        if (areChannelsSelected) classNames.push('full-width');
                        if (isMovie) classNames.push('is-movie');
                    } else {
                        if (!areChannelsSelected) classNames.push('full-width');
                        classNames.push('is-past-mode');
                    }
                    break;

                case 'left': // WEST logic
                    if (!pastMode) {
                        // Future mode
                        if (!areChannelsSelected) {
                            classNames.push('full-width');
                        } else {
                            // Remove 'full-width' if it exists
                            // (not directly handled in classNames array)
                            const index = classNames.indexOf('full-width');
                            if (index !== -1) classNames.splice(index, 1);
                        }
                    } else {
                        // Past mode
                        if (areChannelsSelected) classNames.push('full-width');
                        if (isMovie) classNames.push('is-movie');
                    }
                    break;
                default:
            }

            // Ensure 'is-past-mode' is added for 'left' direction in past mode
            if (direction === 'left' && pastMode) {
                classNames.push('is-past-mode');
            }

            return classNames.filter(Boolean).join(' ');
        },
        [pastMode, areChannelsSelected, isMovie, oneLineGuideTurboScroll, isActive],
    );

    const upCaretVisibility =
        selectedChannelIndex !== null &&
        selectedChannelIndex >= 0 &&
        selectedChannelIndex < FAVORITES_MIN_CHANNELS_IN_LIST
            ? 'is-favorites'
            : '';

    const downCaretVisibility =
        selectedChannelIndex &&
        selectedChannelIndex > FAVORITES_MIN_CHANNELS_IN_LIST &&
        selectedChannelIndex < FAVORITES_MAX_CHANNELS_IN_LIST
            ? 'is-favorites'
            : '';

    return (
        <React.Fragment>
            <Caret
                direction={InteractiveComponents.EAST}
                visible={visibleRightCaret}
                active={isActive(InteractiveComponents.EAST) && !oneLineGuideTurboScroll}
                className={getClassNames('right')}
            />

            <Caret
                direction={InteractiveComponents.WEST}
                visible={visibleLeftCaret}
                active={isActive(InteractiveComponents.WEST) && !oneLineGuideTurboScroll}
                className={getClassNames('left')}
            />

            <Caret
                direction={InteractiveComponents.NORTH}
                visible={true}
                active={
                    oneLineGuideTurboScroll
                        ? oneLineGuideTurboScrollDirection === InteractiveComponents.NORTH
                        : isActive(InteractiveComponents.NORTH)
                }
                className={[
                    'olg-north',
                    oneLineGuideTurboScroll &&
                        oneLineGuideTurboScrollDirection === InteractiveComponents.NORTH &&
                        'active',
                    isActive(InteractiveComponents.NORTH) && !oneLineGuideTurboScroll && 'active',
                    areChannelsSelected && 'full-width',
                    upCaretVisibility,
                    pastMode && 'is-past-mode',
                ]
                    .filter(Boolean)
                    .join(' ')}
            />

            <Caret
                direction={InteractiveComponents.SOUTH}
                visible={true}
                active={
                    oneLineGuideTurboScroll
                        ? oneLineGuideTurboScrollDirection === InteractiveComponents.SOUTH
                        : isActive(InteractiveComponents.SOUTH)
                }
                className={[
                    'olg-south',
                    oneLineGuideTurboScroll &&
                        oneLineGuideTurboScrollDirection === InteractiveComponents.SOUTH &&
                        'active',
                    isActive(InteractiveComponents.SOUTH) && !oneLineGuideTurboScroll && 'active',
                    selectedChannelIndex !== null && 'full-width',
                    downCaretVisibility,
                    pastMode && 'is-past-mode',
                ]
                    .filter(Boolean)
                    .join(' ')}
            />

            <Mousetrap
                className={selectedChannelClassName}
                value={selectedChannelClassName}
                style={{ top: olgSelectedChannelTopOffset }}
            ></Mousetrap>
            <Mousetrap
                className={`${selectedCellClassName} ${pastMode ? 'past-mode' : ''} ${isMovie ? 'movie' : ''}`}
                value={selectedCellClassName}
            ></Mousetrap>
        </React.Fragment>
    );
};

export default OneLineGuideInteractiveLayer;
